import { useState } from "react";
import { CustomDropdown } from "@/components/ui/dropdown/custom-dropdown";
import { Button } from "../../../components/ui/button";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Checkbox } from "../../../components/ui/form";

/* Prop Types */
interface CreatedByDropdownProps {
  teamMembers: { id: string; username: string }[];
  onSelectionChange: (selectedIds: string[]) => void;
  isDragonfly: boolean | null;
  setIsDragonfly: (isDragonfly: boolean) => void;
}

/*
 * CreatedByDropdown Component
 *
 * This component renders a dropdown for filtering contexts based on who made them.
 * Dragonfly is not a "user" in the backend so we handle it separately with a isDragonfly boolean.
 */
export const CreatedByDropdown = ({ teamMembers, onSelectionChange, isDragonfly, setIsDragonfly }: CreatedByDropdownProps) => {
  /* State */
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  /* Handlers */
  const handleCheckboxChange = (id: string) => {
    if (id === "all") {
      // all logic reacts differently depending on if it's already selected or not
      // if all is selected, we need to unselect all including isDragonfly
      // if all is not selected, we need to select all including isDragonfly
      const isAllSelected = selectedIds.includes("all") || selectedIds.length === teamMembers.length;
      setIsDragonfly(!isAllSelected);
      const newSelectedIds = isAllSelected ? [] : teamMembers.map((member) => member.id);
      setSelectedIds(newSelectedIds);
      onSelectionChange(newSelectedIds);
    } else if (id === "dragonfly") {
      // handles the dragonfly checkbox
      setIsDragonfly(!isDragonfly);
    } else {
      // handles the user checkbox selection
      const newSelectedIds = selectedIds.includes(id) ? selectedIds.filter((selectedId) => selectedId !== id) : [...selectedIds.filter((selectedId) => selectedId !== "all"), id];
      setSelectedIds(newSelectedIds);
      onSelectionChange(newSelectedIds);
    }
  };

  /* Render */
  return (
    <CustomDropdown
      triggerBtn={
        <Button>
          Created&nbsp;by
          {/* We only want to show the indicator if we have a user selected or dragonfly is selected and we don't want to show it if all options are selected */}
          {/* Due to isDragonfly needing to be a boolean but being part of the "users" list we couldn't do simple length checks */}
          {(selectedIds.length > 0 || isDragonfly) && !(selectedIds.length === teamMembers.length && isDragonfly) && (
            <span className="indicator-item flex justify-center items-center rounded-full bg-primary size-5 text-white">
              {isDragonfly ? selectedIds.length + 1 : selectedIds.length}
            </span>
          )}
          <ChevronDownIcon className="size-4" />
        </Button>
      }
      triggerBtnClassname="p-0"
    >
      <div className="p-2">
        <label className="flex items-center gap-2">
          <Checkbox checked={selectedIds.length === teamMembers.length && !!isDragonfly} size="sm" onChange={() => handleCheckboxChange("all")} label="All" />
        </label>
        <div className="mt-2">
          <h4 className="font-semibold text-sm mb-1">Your team</h4>
          {teamMembers.map((member) => (
            <Checkbox key={member.id} checked={selectedIds.includes(member.id)} size="sm" onChange={() => handleCheckboxChange(member.id)} label={member.username} />
          ))}
        </div>
        <div className="mt-2">
          <h4 className="font-semibold text-sm mb-1">Dragonfly</h4>
          <Checkbox checked={isDragonfly ? true : false} size="sm" onChange={() => handleCheckboxChange("dragonfly")} label="Dragonfly" />
        </div>
      </div>
    </CustomDropdown>
  );
};
